import React, { memo } from "react";
import { Card, Col, Input, Row, Segmented, Tooltip } from "antd";
import { ICapabilityDetails, IRoleDetails } from "@Models/IRoleDetails";

interface IProps {
    handleCapabilitySearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleCapabilityChange: (capabilityId: string, value: string | number) => Promise<void>;
    capabilitySearch: string;
    isSystemAdminRole: boolean;
    hasCapabilityUpdate: boolean;
    role: IRoleDetails;
    capabilities: ICapabilityDetails[];
}

const CapabilitiesTable: React.FC<IProps> = ({
    handleCapabilitySearch,
    capabilitySearch,
    isSystemAdminRole,
    hasCapabilityUpdate,
    handleCapabilityChange,
    role,
    capabilities,
}: IProps) => {
    return (
        <Row gutter={[16, 8]} className="roles-page-row">
            <Col md={24}>
                <Card className="newUI-subtle">
                    <Input
                        value={capabilitySearch}
                        placeholder="Filter capabilities..."
                        allowClear
                        onChange={handleCapabilitySearch}
                        style={{ width: 250, marginBottom: 30 }}
                    />
                    <div className="table-wrapper">
                        <table className="table newUI-glass">
                            <thead>
                                <tr>
                                    <th>Capability name</th>
                                    <th>Description</th>
                                    <th>Context</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {capabilities?.map((item) => {
                                    const roleCap = role.roleCapabilities?.find(
                                        (x) => x.capabilityId === item.capabilityId
                                    );
                                    const cap = capabilities?.find((x) => x.capabilityId === item.capabilityId);
                                    return (
                                        <tr key={"role-capability-" + item.capabilityId} className="table-row">
                                            <td className="table-cell table-cell-heading">{cap?.name}</td>
                                            <td className="table-cell desc">{cap?.description}</td>
                                            <td className="table-cell segmented">
                                                <div className="flex flex-justify-space-between">
                                                    <Segmented
                                                        className={"newUI-raised"}
                                                        disabled={isSystemAdminRole || !hasCapabilityUpdate}
                                                        options={[
                                                            {
                                                                label: (
                                                                    <Tooltip title={"Disable in all contexts"}>
                                                                        {"None"}
                                                                    </Tooltip>
                                                                ),
                                                                value: 0,
                                                            },
                                                            {
                                                                label: (
                                                                    <Tooltip title={"Allow everywhere"}>
                                                                        {"System"}
                                                                    </Tooltip>
                                                                ),
                                                                value: 1,
                                                                disabled: !cap?.availableContexts.includes(1),
                                                            },
                                                            {
                                                                label: (
                                                                    <Tooltip
                                                                        title={"Allow within assigned groups only"}
                                                                    >
                                                                        {"Group"}
                                                                    </Tooltip>
                                                                ),
                                                                value: 2,
                                                                disabled: !cap?.availableContexts.includes(2),
                                                            },
                                                            {
                                                                label: (
                                                                    <Tooltip
                                                                        title={"Allow for the user's own data only"}
                                                                    >
                                                                        {"User"}
                                                                    </Tooltip>
                                                                ),
                                                                value: 3,
                                                                disabled: !cap?.availableContexts.includes(3),
                                                            },
                                                        ]}
                                                        value={roleCap?.contextId}
                                                        onChange={(value) =>
                                                            handleCapabilityChange(item.capabilityId, value)
                                                        }
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </Col>
        </Row>
    );
};

export default memo(CapabilitiesTable);
